import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import {
  ToQueryStringFilters,
  useDate,
  usePagination,
  useToQueryString,
} from '@/core'
import type { TPaymentInvoice } from '../../../types'
import { Pagination } from '@/core/types'
import { useProvidePaymentsInvoiceQuery } from '../query/usePaymentsQuery'
import { storeToRefs } from 'pinia'
import { useCatalogStore } from '@/core/store/catalogs'
import { useModuleQuery } from '../query/useModuleQuery'
import { useInvoicesQuery } from '../query/useInvoicesQuery'

const [useProvidePaymentsInvoice, usePaymentsInvoice] = createInjectionState(
  () => {
    const rows = ref<number[]>([])
    const form = ref<TPaymentInvoice>({})
    const projectId = ref<number>()
    const modelId = ref<number>()
    const store = useCatalogStore()
    const { zonedTimeToUtc } = useDate()
    const { catalogs } = storeToRefs(store)
    const filters = ref<ToQueryStringFilters>({
      param: '',
      dates: {
        created_at: [],
      },
      order_by: 'created_at,desc',
    })

    const moduleQueryParams = useToQueryString(toReactive(filters), {
      searchColumns: ['name'],
    })

    const modulePagination = usePagination()

    const moduleQuery = useModuleQuery({
      enableQuery: {
        loadRecords: false,
        loadRecord: false,
      },
    })

    const invoiceQuery = useInvoicesQuery({
      modelId: computed(() => Number(projectId.value)),
      modelChildId: computed(() => Number(modelId.value)),
      enableQuery: {
        loadRecord: computed(() => !!projectId.value),
        loadRecords: false,
      },
    })

    const paymentsInvoiceQuery = useProvidePaymentsInvoiceQuery({
      modelId: computed(() => Number(modelId.value)),
      currentPage: modulePagination.currentPage,
      filters: {
        loadRecords: moduleQueryParams.queryParams,
      },
      enableQuery: {
        loadRecords: computed(
          () => !!moduleQueryParams.params.value.length && !!modelId.value,
        ),
        loadInvoice: computed(() => !!modelId.value),
      },
      handlers: {
        onDataChange: (pagination: Pagination) => {
          modulePagination.setPagination(pagination)
        },
      },
    })

    const onAdd = async (payload: TPaymentInvoice) => {
      await paymentsInvoiceQuery.addRecord.mutateAsync({
        ...payload,
        date: payload.date
          ? zonedTimeToUtc(new Date(payload.date ?? ''), {
              removeTimeZone: true,
            })
          : payload.date,
      })
      await invoiceQuery.loadRecord.refetch()
      await invoiceQuery.serviceHandlers.invalidateQueries()
    }

    const onEdit = async (payload: TPaymentInvoice) => {
      await paymentsInvoiceQuery.editRecord.mutateAsync({
        ...payload,
        date: payload.date
          ? zonedTimeToUtc(new Date(payload.date ?? ''), {
              removeTimeZone: true,
            })
          : payload.date,
      })
      await invoiceQuery.loadRecord.refetch()
      await invoiceQuery.serviceHandlers.invalidateQueries()
    }

    const onDelete = async (payload: TPaymentInvoice) => {
      await paymentsInvoiceQuery.deleteRecord.mutateAsync(payload)
      await invoiceQuery.loadRecord.refetch()
      await invoiceQuery.serviceHandlers.invalidateQueries()
    }

    const onLoadInvoiceRecord = async (payload: number, project: number) => {
      modelId.value = payload
      projectId.value = project
      paymentsInvoiceQuery.loadRecords.refetch()
    }

    const onRequestCatalog = async (payload: number[]) => {
      await moduleQuery.requestCatalog.mutateAsync({ ids: payload })
    }

    const tableData = computed(
      () => paymentsInvoiceQuery.loadRecords.data?.value?.data,
    )
    const model = computed(() => invoiceQuery.loadRecord?.data.value)

    const isLoading = computed(
      () =>
        paymentsInvoiceQuery.addRecord.isLoading.value ||
        paymentsInvoiceQuery.editRecord.isLoading.value ||
        paymentsInvoiceQuery.loadRecords.isLoading.value,
    )

    const isLoadingCategories = computed(
      () => moduleQuery.requestCatalog.isLoading.value,
    )

    return {
      tableData,
      filters,
      rows,
      form,
      isLoading,
      model,
      isLoadingCategories,
      catalogs,
      modulePagination: toReactive(modulePagination),
      onRequestCatalog,
      onDelete,
      onEdit,
      onAdd,
      onLoadInvoiceRecord,
    }
  },
)

export { useProvidePaymentsInvoice, usePaymentsInvoice }
